import type { ComponentDefinition, FormComponent, FormDocument, FormParameters, FormTemplate } from '@formbird/types';
import { createSignal, createEffect, onCleanup, For, Show } from 'solid-js';
import { Subscription } from 'rxjs';

interface PartyComponentDefinition extends ComponentDefinition {
  dropDownList?: string[];
  mandatoryFields?: string[];
  mandatoryValues?: any;
}

interface PartyProps {
  document: FormDocument;
  template: FormTemplate;
  componentDefinition: PartyComponentDefinition;
  fieldValue: any;
  fieldName: string;
  previousFieldValue: any;
  key: number;
  formParameters: FormParameters;
}

interface PartyValue {
  title?: string;
  firstName?: string;
  surname?: string;
}

export function Party(props: PartyProps) {
  const subs = new Subscription();

  const [title, setTitle] = createSignal('');
  const [firstName, setFirstName] = createSignal('');
  const [surname, setSurname] = createSignal('');
  const [componentDef, setComponentDef] = createSignal(props.componentDefinition);
  const [titleOptions, setTitleOptions] = createSignal<string[]>(['Mr', 'Mrs', 'Miss', 'Ms', 'Dr']);

  const changedDocumentService = (globalThis as any).FormbirdServiceInjector.get('ChangedDocumentService');

  // Initialize options
  createEffect(() => {
    if (componentDef()?.dropDownList) {
      setTitleOptions(componentDef()?.dropDownList || []);
    }
  });

  // Initialize values
  createEffect(() => {
    const val = props.fieldValue || props.document[props.fieldName] || {};
    setTitle(val.title || '');
    setFirstName(val.firstName || '');
    setSurname(val.surname || '');
  });

  const docSub = changedDocumentService
    .watch(props.fieldName, props.document.documentId, null)
    .subscribe((newValue: any) => {
      const val = newValue || {};
      setTitle(val.title || '');
      setFirstName(val.firstName || '');
      setSurname(val.surname || '');
    });
  subs.add(docSub);

  const compSub = changedDocumentService
    .watchComponentDef(props.fieldName, props.template.documentId)
    .subscribe((compDef: any) => {
      setComponentDef({ ...componentDef(), ...compDef });
    });
  subs.add(compSub);

  onCleanup(() => {
    subs.unsubscribe();
  });

  const isDisabled = () => componentDef()?.enabled === false;
  
  const isMandatory = (field: string) => {
    if (!componentDef()?.mandatory) return false;
    const mandatoryFields = componentDef()?.mandatoryFields;
    if (!mandatoryFields || mandatoryFields.length === 0) return true; // If mandatory but no specific fields, all are mandatory (default behavior in Angular seems to be all if empty)
    return mandatoryFields.includes(field);
  };

  const labelColor = (field: string) => isDisabled() ? 'text-gray-500' : isMandatory(field) ? 'text-red-500' : 'text-gray-900';
  const borderStyle = () => isDisabled() ? 'border-dashed' : 'border-solid';
  
  const inputClasses = () => `
    peer h-full w-full border-b ${borderStyle()} bg-transparent pt-4 pb-1.5
    font-sans text-sm font-normal outline outline-0 transition-all
    text-gray-900 border-gray-800
    focus:border-gray-900 focus:outline-0
    ${isDisabled() ? 'cursor-not-allowed' : ''}
  `;

  const updateValue = (newTitle: string, newFirstName: string, newSurname: string) => {
    const newValue: PartyValue = {
      title: newTitle,
      firstName: newFirstName,
      surname: newSurname
    };

    // Filter out empty strings if needed, or keep them. Angular implementation keeps them but might delete keys if empty and mandatory logic applies.
    // For simplicity, we'll send the object. 
    // Angular logic has complex mandatory checks that might delete keys. We will replicate basic "send what we have".
    
    // Check for empty party
    if (!newTitle && !newFirstName && !newSurname) {
        changedDocumentService.valueChanged({
            document: props.document,
            template: props.template,
            componentDefinition: componentDef(),
            fieldValue: undefined,
            fieldName: props.fieldName,
            formParameters: props.formParameters,
            key: props.key,
        }, undefined);
        return;
    }

    const changeInfo: FormComponent = {
      document: props.document,
      template: props.template,
      componentDefinition: componentDef(),
      fieldValue: newValue,
      fieldName: props.fieldName,
      formParameters: props.formParameters,
      key: props.key,
    };

    changedDocumentService.valueChanged(changeInfo, newValue);
  };

  const handleTitleChange = (e: Event) => {
    const val = (e.target as HTMLSelectElement).value;
    setTitle(val);
    updateValue(val, firstName(), surname());
  };

  const handleFirstNameChange = (e: Event) => {
    const val = (e.target as HTMLInputElement).value;
    setFirstName(val);
    updateValue(title(), val, surname());
  };

  const handleSurnameChange = (e: Event) => {
    const val = (e.target as HTMLInputElement).value;
    setSurname(val);
    updateValue(title(), firstName(), val);
  };

  return (
    <div class="flex flex-col gap-4 w-full">
      <div class="text-sm font-medium text-gray-700">
        {componentDef()?.label}
        {componentDef()?.mandatory && <span class="text-red-500"> *</span>}
      </div>
      
      <div class="flex flex-col md:flex-row gap-4">
        {/* Title */}
        <div class="relative h-11 w-full md:w-1/4">
          <select
            id={`party-title-${props.document.documentId}-${props.fieldName}`}
            class={inputClasses()}
            value={title()}
            onChange={handleTitleChange}
            disabled={isDisabled()}
          >
            <option value=""></option>
            <For each={titleOptions()}>
              {(option) => <option value={option}>{option}</option>}
            </For>
          </select>
          <label for={`party-title-${props.document.documentId}-${props.fieldName}`} class={`pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none !overflow-visible truncate text-[11px] font-normal leading-tight ${labelColor('title')} transition-all after:absolute after:-bottom-1.5 after:block after:w-full after:scale-x-0 after:border-b-2 after:border-gray-500 after:transition-transform after:duration-300 peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.25] peer-placeholder-shown:text-blue-gray-500 peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-900 peer-focus:after:scale-x-100 peer-focus:after:border-gray-900 peer-disabled:text-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500`}>
            Salutation
            {isMandatory('title') && ' *'}
          </label>
        </div>

        {/* First Name */}
        <div class="relative h-11 w-full md:w-1/3">
          <input
            id={`party-firstName-${props.document.documentId}-${props.fieldName}`}
            class={inputClasses()}
            placeholder="First Name"
            value={firstName()}
            onInput={handleFirstNameChange}
            disabled={isDisabled()}
          />
          <label for={`party-firstName-${props.document.documentId}-${props.fieldName}`} class={`pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none !overflow-visible truncate text-[11px] font-normal leading-tight ${labelColor('firstName')} transition-all after:absolute after:-bottom-1.5 after:block after:w-full after:scale-x-0 after:border-b-2 after:border-gray-500 after:transition-transform after:duration-300 peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.25] peer-placeholder-shown:text-blue-gray-500 peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-900 peer-focus:after:scale-x-100 peer-focus:after:border-gray-900 peer-disabled:text-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500`}>
            First Name
            {isMandatory('firstName') && ' *'}
          </label>
        </div>

        {/* Surname */}
        <div class="relative h-11 w-full md:w-1/3">
          <input
            id={`party-surname-${props.document.documentId}-${props.fieldName}`}
            class={inputClasses()}
            placeholder="Surname"
            value={surname()}
            onInput={handleSurnameChange}
            disabled={isDisabled()}
          />
          <label for={`party-surname-${props.document.documentId}-${props.fieldName}`} class={`pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none !overflow-visible truncate text-[11px] font-normal leading-tight ${labelColor('surname')} transition-all after:absolute after:-bottom-1.5 after:block after:w-full after:scale-x-0 after:border-b-2 after:border-gray-500 after:transition-transform after:duration-300 peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.25] peer-placeholder-shown:text-blue-gray-500 peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-900 peer-focus:after:scale-x-100 peer-focus:after:border-gray-900 peer-disabled:text-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500`}>
            Surname
            {isMandatory('surname') && ' *'}
          </label>
        </div>
      </div>
    </div>
  );
}
